import { applyQuickAddMatches, scanForWordLinks } from '@/lib/word-links';
import type { NoyauKey, WordSummary } from '@/schemas/word';
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

const NOYAU_COLOR: Record<NoyauKey, string> = {
  bleunuit: 'var(--color-pms-bleu-nuit)',
  bleuciel: 'var(--color-pms-bleu-ciel)',
  vert: 'var(--color-pms-vert)',
  jaune: 'var(--color-pms-jaune-deep)',
  rouge: 'var(--color-pms-rouge)',
  rose: 'var(--color-pms-rose)',
};

interface QuickAddLinksModalProps {
  content: string;
  wordList: WordSummary[];
  /** True when `wordList` is a capped scan of the catalog — more linkable words may exist beyond it. */
  truncated?: boolean;
  onApply: (updatedContent: string) => void;
  onClose: () => void;
}

/** Checklist modal that scans the body for unlinked word titles and lets the user apply them as [text](slug) links. */
export function QuickAddLinksModal({
  content,
  wordList,
  truncated = false,
  onApply,
  onClose,
}: QuickAddLinksModalProps) {
  const matches = useMemo(() => scanForWordLinks(content, wordList), [content, wordList]);
  const [checked, setChecked] = useState<Set<string>>(() => new Set(matches.map((m) => m.id)));

  function toggle(id: string) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleApply() {
    const selected = matches.filter((m) => checked.has(m.id));
    onApply(applyQuickAddMatches(content, selected));
    onClose();
  }

  const modal = (
    <dialog
      open
      className="qal-overlay"
      aria-modal="true"
      aria-label="Ajouter des liens automatiquement"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="qal">
        <div className="qal__header">
          <span className="qal__title">Liens automatiques</span>
          <button type="button" className="qal__close" aria-label="Fermer" onClick={onClose}>
            <span className="material-symbols-rounded">close</span>
          </button>
        </div>

        {matches.length === 0 ? (
          <p className="qal__empty">Aucun mot du dictionnaire trouvé dans le corps du texte.</p>
        ) : (
          <>
            <p className="qal__hint">
              {matches.length} occurrence{matches.length > 1 ? 's' : ''} trouvée
              {matches.length > 1 ? 's' : ''}. Sélectionnez celles à transformer en lien.
              {truncated && ' Recherche limitée aux mots les plus pertinents du dictionnaire.'}
            </p>
            <ul className="qal__list">
              {matches.map((match) => {
                const word = match.wordSummary;
                const color = word.dominantNoyauKey
                  ? NOYAU_COLOR[word.dominantNoyauKey]
                  : 'var(--color-pms-gray-400)';
                return (
                  <li key={match.id} className="qal__item">
                    <label className="qal__row">
                      <input
                        type="checkbox"
                        className="qal__checkbox"
                        checked={checked.has(match.id)}
                        onChange={() => toggle(match.id)}
                      />
                      <span className="qal__word" style={{ color }}>
                        {word.title}
                      </span>
                      <span className="qal__context">
                        {highlightMatch(match.context, match.matchText)}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </>
        )}

        <div className="qal__actions">
          <button type="button" className="qal__btn qal__btn--cancel" onClick={onClose}>
            Annuler
          </button>
          {matches.length > 0 && (
            <button
              type="button"
              className="qal__btn qal__btn--apply"
              disabled={checked.size === 0}
              onClick={handleApply}
            >
              Appliquer ({checked.size})
            </button>
          )}
        </div>
      </div>
    </dialog>
  );

  return createPortal(modal, document.body);
}

/** Wraps the first occurrence of matchText in a <mark> element for the context preview snippet. */
function highlightMatch(context: string, matchText: string): React.ReactNode {
  const idx = context.toLowerCase().indexOf(matchText.toLowerCase());
  if (idx === -1) return context;
  return (
    <>
      {context.slice(0, idx)}
      <mark>{context.slice(idx, idx + matchText.length)}</mark>
      {context.slice(idx + matchText.length)}
    </>
  );
}
